import { StateEffect, StateField, Transaction } from "@codemirror/state";
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import deepEqual from "fast-deep-equal";
import { reaction } from "mobx";
import { ActionType } from "../Types";
import { canvasDispatch } from "./CanvasDispatch";
import { getPresencesForPaneId, presenceState } from "./Presence";
import { applicationState } from "./Sync";

export function presenceDispatchPlugin(paneId: string) {
  return ViewPlugin.fromClass(
    class {
      update(update: ViewUpdate) {
        const isRemote = update.transactions.some((transaction) =>
          transaction.annotation(Transaction.remote)
        );
        if (!isRemote) {
          const selectionUpdate = update.transactions.find(
            (tr) => tr.selection !== undefined
          )?.selection;
          if (selectionUpdate !== undefined) {
            canvasDispatch([
              ActionType.PresenceTextRange,
              [paneId, [selectionUpdate.main.from, selectionUpdate.main.to]],
            ]);
          }
        }
      }
    }
  );
}

const setPresenceCursors = StateEffect.define<
  {
    clientId: string;
    textRange?: [from: number, to: number] | undefined;
  }[]
>();

class PresenceCursorWidget extends WidgetType {
  constructor(readonly clientId: string, readonly color: string) {
    super();
  }

  eq(other: PresenceCursorWidget) {
    return this.clientId === other.clientId && this.color === other.color;
  }

  toDOM() {
    const wrap = document.createElement("span");
    wrap.setAttribute("aria-hidden", "true");
    wrap.className = "relative -left-0.5 pointer-events-none";
    const line = document.createElement("span");
    line.className = "w-0.5 left-0 h-4 absolute";
    line.style.backgroundColor = `#${this.color}`;
    wrap.appendChild(line);
    return wrap;
  }
}

const presenceSelectionField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(selections, tr) {
    for (let e of tr.effects) {
      if (e.is(setPresenceCursors)) {
        const newDocLength = tr.newDoc.length;
        return Decoration.set(
          e.value.flatMap(({ clientId, textRange }) => {
            if (textRange === undefined) {
              return [];
            }
            if (textRange[1] > newDocLength) {
              // ideally this shouldn't happen but safeguard this.
              return [];
            }
            const color = presenceState[clientId].color;
            const decorations = [
              Decoration.widget({
                widget: new PresenceCursorWidget(clientId, color),
              }).range(textRange[0]),
            ];
            if (textRange[0] < textRange[1]) {
              const cursor = Decoration.mark({
                attributes: {
                  style: `background-color: #${color}20`,
                },
              }).range(textRange[0], textRange[1]);
              cursor.value.spec.clientId = clientId;
              decorations.push(cursor);
            }
            return decorations;
          }),
          true
        );
      }
    }
    try {
      // TODO: investigate why this can throw sometimes
      // The selection position can be out of bounds.
      return selections.map(tr.changes);
    } catch {
      return Decoration.none;
    }
  },
  provide: (f) => EditorView.decorations.from(f),
});

export function subscribeCodemirrorPaneId(paneId: string, cmView: EditorView) {
  return reaction(
    () =>
      getPresencesForPaneId(paneId).filter(
        ({ clientId }) => clientId !== `${applicationState.clientId}`
      ),
    (presences) => {
      const effects: StateEffect<unknown>[] = [
        setPresenceCursors.of(presences),
      ];
      if (cmView.state.field(presenceSelectionField, false) === undefined) {
        effects.push(StateEffect.appendConfig.of([presenceSelectionField]));
      }
      cmView.dispatch({ effects });
    },
    { equals: deepEqual }
  );
}
